/**
 * Rerun 새 창 뷰어의 창 핸들.
 *
 * Rerun 새 창 뷰어는 BE 가 빌드한 껍데기다. 껍데기는 FE origin 의 `/rerun/` 에 있다. 껍데기는 로그인
 * 세션의 JWT 로 뷰어 세션을 만들고, Revision 에 저장된 매핑을 쓴다. FE 는 창을 열고 핸들을 기억한다.
 *
 * 세션이 끝나면 `AppStateProvider` 가 `closeRerunWindows()` 를 부른다(규칙 §8). 껍데기는 로그아웃을
 * 감지하지 않는다. 그래서 FE 가 창을 닫지 않으면 로그아웃 뒤에도 조직 데이터가 창에 남는다.
 *
 * Owns:
 * - 껍데기 주소, 창 이름, 열린 창 핸들, 버튼을 끄는 사유.
 *
 * Does not own:
 * - 뷰어 세션 생성 · grant 갱신 · 재접속 — 껍데기(BE `packages/spnp/viewer-shell`).
 */
import type { StoredMappingState } from "./mappingEditor";

const SHELL_PATH = "/rerun/";
const WINDOW_NAME_PREFIX = "spawning-pool-rerun";

// 창 이름 → 핸들. 같은 episode 는 같은 이름을 쓰므로 창 하나만 남는다.
const openWindows = new Map<string, Window>();

/** 껍데기 주소. 예) `/rerun/?probe=pr-1&source_id=episode_000000`. JWT 는 주소에 넣지 않는다. */
export function rerunShellUrl(probeUuid: string, sourceId: string): string {
  const query = new URLSearchParams({ probe: probeUuid, source_id: sourceId });
  return `${SHELL_PATH}?${query.toString()}`;
}

/**
 * episode 하나의 Rerun 새 창 뷰어를 연다. 팝업이 막히면 `false` 다.
 *
 * 같은 episode 의 창이 이미 열려 있으면 다시 불러오지 않고 앞으로 가져온다. 다시 불러오면 껍데기가
 * 새 뷰어 세션을 만든다. `noopener` 는 쓰지 않는다. `noopener` 로 열면 핸들이 없어 로그아웃 때 닫을 수
 * 없다. 껍데기는 같은 origin 의 우리 페이지다.
 */
export function openRerunWindow(probeUuid: string, sourceId: string): boolean {
  const name = `${WINDOW_NAME_PREFIX}:${probeUuid}:${sourceId}`;
  const existing = openWindows.get(name);
  if (existing !== undefined && !existing.closed) {
    existing.focus();
    return true;
  }
  const handle = window.open(rerunShellUrl(probeUuid, sourceId), name);
  if (handle === null) {
    openWindows.delete(name);
    return false;
  }
  openWindows.set(name, handle);
  return true;
}

/** 이 탭이 연 Rerun 새 창 뷰어를 모두 닫는다. 두 번 불러도 안전하다. */
export function closeRerunWindows(): void {
  for (const handle of openWindows.values()) {
    if (!handle.closed) {
      handle.close();
    }
  }
  openWindows.clear();
}

/**
 * Rerun 새 창 버튼을 끄는 사유. 켤 수 있으면 `null` 이다.
 *
 * 뷰어 세션은 Revision 에 저장된 매핑을 쓴다. 저장본이 없으면 껍데기가 `mapping.not_found` 로 멈춘다.
 * 그래서 저장본이 확인될 때만 버튼을 켠다.
 */
export function rerunUnavailableReason(state: StoredMappingState): string | null {
  switch (state.status) {
    case "saved":
      return null;
    case "loading":
      return "저장된 매핑을 확인하는 중입니다.";
    case "no_revision":
      return "이 Probe 에는 Revision 이 없어 Rerun 새 창을 열 수 없습니다.";
    case "none":
      return "저장된 매핑이 없습니다. 매핑을 저장한 뒤 다시 열어 주세요.";
    case "failed":
      return "저장된 매핑을 확인하지 못해 Rerun 새 창을 열 수 없습니다.";
  }
}
